<template>
  <div v-if="displayWorkflowFallbackMessage" data-qa="preview-content-inapp">
    <div class="fallback-msg-ct">
      {{ $t('CampaignForm_Preview_ContentMessageDetails_NoMessages') }}
    </div>
  </div>
  <div v-else data-qa="preview-content-inapp">
    <inapp-test-send-dialog
      v-if="canShowSendPreview"
      :dialog-visible.sync="showSendPreview"
      :preview="previewSendData"
      :html="previewHtml"
      :inapp-data="previewInappData"
      :inapp-native="isNative"
    />
    <div class="flexbox">
      <div class="controls-container">
        <el-radio-group
          v-if="showOrientationToggle"
          v-model="activeOrientation"
          size="small"
          data-qa="preview-inapp-orientation-toggle"
        >
          <el-radio-button
            label="portrait"
            data-qa="preview-inapp-orientation-portrait"
          >
            {{ $t('CampaignForm_InApp_Tab_Portrait') }}
          </el-radio-button>
          <el-radio-button
            label="landscape"
            data-qa="preview-inapp-orientation-landscape"
          >
            {{ $t('CampaignForm_InApp_Tab_Landscape') }}
          </el-radio-button>
        </el-radio-group>
        <el-select
          v-if="!isNative"
          v-model="screenSize"
          placeholder="Select"
          value-key="label"
          data-qa="preview-inapp-screen-size-select"
        >
          <el-option-group
            v-for="group in screenSizesOptions"
            :key="group.label"
            :label="group.label"
          >
            <el-option
              v-for="item in group.options"
              :key="item.label"
              :label="item.label"
              :value="item"
            />
          </el-option-group>
        </el-select>
      </div>
    </div>
    <div v-loading="loading" data-qa="preview-inapp-body">
      <div
        v-if="isNative"
        class="native-preview-container"
        data-qa="preview-inapp-native"
      >
        <div class="native-payload">
          <div class="native-payload-header">
            {{ $t('CampaignForm_Preview_Native_Payload') }}
          </div>
          <pre
            class="native-payload-content"
            data-qa="preview-inapp-native-payload"
            >{{ content.inappData }}</pre
          >
        </div>
      </div>
      <div v-else :class="{ 'preview-body': hasActionDetails }">
        <div
          class="inapp-preview-container"
          :class="{ 'inapp-preview-container--split': hasActionDetails }"
          data-qa="preview-inapp-device"
        >
          <phone-new :width="frameWidth" :height="frameHeight" :type="isTablet">
            <div class="html-content">
              <iframe
                v-if="content.html"
                :key="`${deviceWidth}x${deviceHeight}`"
                :srcdoc="content.html"
                class="inapp-html-preview"
                :style="iframeStyle"
                sandbox="allow-scripts"
                data-qa="preview-inapp-html-iframe"
              />
            </div>
          </phone-new>
        </div>
        <div
          v-if="hasActionDetails"
          class="preview-details"
          data-qa="preview-inapp-action-details"
        >
          <preview-default-action
            :url="defaultActionUrl"
            :deeplink="defaultActionDeeplink"
            :action-type="defaultActionType"
            data-qa-prefix="preview-inapp"
          />
          <preview-action-buttons
            :buttons="buttonActionItems"
            data-qa-prefix="preview-inapp"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import RestApi from '@/core/services/RestApi'
import { projectEndpoint } from '@/core/helpers'
import { channelTypes } from '@/modules/campaign-manage/enum/Labels'
import { PreviewChannelEnum } from '@/modules/campaign-manage/enum/PreviewChannelEnum'
import { EventBus } from '@/core/EventBus'
import { Message } from 'element-ui'
import _ from 'lodash'
import PhoneNew from '@/common/components/phone/PhoneNew.vue'
import InappTestSendDialog from '@/modules/campaign-manage/components/preview/InappTestSendDialog.vue'
import PreviewDefaultAction from '@/modules/campaign-manage/components/preview/PreviewDefaultAction.vue'
import PreviewActionButtons from '@/modules/campaign-manage/components/preview/PreviewActionButtons.vue'
import { screenSizes } from '@/modules/campaign-manage/enum/ScreeenSizes'
import {
  processCustomAttributes,
  normaliseUnmetConditions
} from '@/modules/campaign-manage/helpers/PreviewAttributeHelper'

const INAPP_TYPE_BANNER = 0
const INAPP_TYPE_FULLSCREEN = 1
const INAPP_TYPE_NATIVE = 4
const ORIENTATION_BOTH = 0
const ORIENTATION_LANDSCAPE = 2

const ORIENTATION_AWARE_TYPES = [INAPP_TYPE_BANNER, INAPP_TYPE_FULLSCREEN]

export default {
  name: 'PreviewContentInApp',
  components: {
    PhoneNew,
    InappTestSendDialog,
    PreviewDefaultAction,
    PreviewActionButtons
  },
  props: {
    model: {
      type: Object,
      required: true
    },
    variantKey: {
      type: String,
      required: true
    },
    workflowAction: {
      type: Object,
      required: false,
      default: () => ({})
    },
    language: {
      type: String,
      required: false
    },
    profileId: {
      type: String,
      required: false,
      default: null
    },
    /**
     * Full profile object corresponding to `profileId` — used by the test-send
     * dialog to render a human label for the selected profile in its description.
     */
    selectedProfile: {
      type: Object,
      required: false,
      default: null
    },
    customAttributes: {
      type: Object,
      required: false,
      default: null
    }
  },
  mounted() {
    if (this.extendedContentPreview) {
      EventBus.$on('showSendPreview', this.onShowSendPreview)
    }
    if (this.displayWorkflowFallbackMessage) {
      this.loading = false
      EventBus.$emit('set-profile-attributes', {})
      EventBus.$emit('set-unmet-attribute-conditions', {})
      return
    }
    this.screenSize = this.screenSizesOptions[0].options[1]
    this.getPreview()
  },
  beforeDestroy() {
    EventBus.$off('showSendPreview', this.onShowSendPreview)
  },
  data() {
    return {
      screenSizesOptions: screenSizes,
      screenSize: {},
      loading: true,
      content: {},
      activeOrientation: 'portrait',
      showSendPreview: false
    }
  },
  computed: {
    isNative() {
      return this.content.inappType === INAPP_TYPE_NATIVE
    },
    resolution() {
      let height = 450
      let width = Math.round(
        (height / (this.screenSize.height || 736)) *
          (this.screenSize.width || 414)
      )
      return { width, height }
    },
    isLandscape() {
      return this.isOrientationAware && this.activeOrientation === 'landscape'
    },
    frameWidth() {
      const px = this.isLandscape
        ? this.resolution.height
        : this.resolution.width
      return px + 'px'
    },
    frameHeight() {
      const px = this.isLandscape
        ? this.resolution.width
        : this.resolution.height
      return px + 'px'
    },
    // The in-app SDK picks creatives and sizes content from the iframe's
    // innerWidth/innerHeight, so lay the iframe out at the device's logical
    // size and scale it down to fit the frame.
    deviceWidth() {
      const width = this.screenSize.width || 414
      const height = this.screenSize.height || 736
      return this.isLandscape ? height : width
    },
    deviceHeight() {
      const width = this.screenSize.width || 414
      const height = this.screenSize.height || 736
      return this.isLandscape ? width : height
    },
    previewScale() {
      return this.resolution.height / (this.screenSize.height || 736)
    },
    iframeStyle() {
      return {
        width: this.deviceWidth + 'px',
        height: this.deviceHeight + 'px',
        transform: `scale(${this.previewScale})`,
        transformOrigin: '0 0'
      }
    },
    isOrientationAware() {
      return ORIENTATION_AWARE_TYPES.includes(this.content.inappType)
    },
    showOrientationToggle() {
      return (
        this.isOrientationAware &&
        this.content.inappOrientation === ORIENTATION_BOTH
      )
    },
    displayWorkflowFallbackMessage() {
      return Boolean(this.model.workflow) && _.isEmpty(this.workflowAction)
    },
    defaultActionUrl() {
      return this.content.defaultActionUrl || ''
    },
    defaultActionDeeplink() {
      return this.content.defaultActionDeeplink || ''
    },
    defaultActionType() {
      const type = this.content.defaultActionType
      return type && type !== 'none' ? type : null
    },
    buttonActions() {
      return (this.content.buttonActions || []).filter(
        button => button.url || button.deeplink
      )
    },
    buttonActionItems() {
      return this.buttonActions.map(button => ({
        label: `${this.$t('CampaignForm_InApp_Button_Title')} ${button.index}`,
        url: button.url,
        deeplink: button.deeplink
      }))
    },
    hasActionDetails() {
      return Boolean(
        this.defaultActionUrl ||
          this.defaultActionDeeplink ||
          this.defaultActionType ||
          this.buttonActions.length
      )
    },
    extendedContentPreview() {
      return !!this.$hasFeature('extendedContentPreview')
    },
    canShowSendPreview() {
      return this.showSendPreview && this.extendedContentPreview
    },
    previewSendData() {
      return {
        campaign: this.model,
        message_type: channelTypes.inapp,
        variant_key: this.variantKey,
        workflow_action: this.workflowAction,
        language: this.language,
        profile_id: this.profileId,
        selected_profile: this.selectedProfile,
        customAttributes: this.processCustomAttributes(this.customAttributes)
      }
    },
    previewHtml() {
      return this.content.html || ''
    },
    previewInappData() {
      return this.content.inappData || ''
    },
    isTablet() {
      return this.screenSize.width > 600 ? 'tablet' : 'phone'
    }
  },
  watch: {
    customAttributes: {
      handler(newVal) {
        if (newVal !== null && newVal !== undefined) {
          this.getPreview()
        }
      },
      deep: true
    }
  },
  methods: {
    processCustomAttributes,
    onShowSendPreview(payload) {
      if (!payload || payload.channel === PreviewChannelEnum.INAPP) {
        this.showSendPreview = true
      }
    },
    getPreview() {
      this.loading = true
      RestApi(
        projectEndpoint({
          method: 'post',
          url: `campaign-messages/preview`,
          data: {
            campaign: this.model,
            message_type: channelTypes.inapp,
            variant_key: this.variantKey,
            workflow_action: this.workflowAction,
            language: this.language,
            profile_id: this.profileId,
            customAttributes: this.processCustomAttributes(
              this.customAttributes
            )
          }
        })
      )
        .then(({ data }) => {
          if (this._isDestroyed) return
          this.content = {
            html: data['message'].html || '',
            inappData: data['message'].inapp_data || '',
            inappType: data['message'].inapp_type,
            inappOrientation: data['message'].inapp_orientation,
            defaultActionUrl: data['message'].default_action_url || '',
            defaultActionDeeplink:
              data['message'].default_action_deeplink || '',
            defaultActionType: data['message'].default_action_type || null,
            buttonActions: data['message'].button_actions || []
          }
          this.activeOrientation =
            this.content.inappOrientation === ORIENTATION_LANDSCAPE
              ? 'landscape'
              : 'portrait'
          EventBus.$emit('set-profile-attributes', data.attributes)
          if (!this.customAttributes && this.profileId) {
            EventBus.$emit('set-profile-id', this.profileId)
          }
          EventBus.$emit(
            'set-unmet-attribute-conditions',
            normaliseUnmetConditions(data['unmetConditions'])
          )
        })
        .catch(error => {
          Message.closeAll()
          this.$message({
            message: error.message,
            type: 'error',
            duration: 5000
          })
        })
        .finally(() => {
          this.loading = false
        })
    }
  }
}
</script>

<style scoped lang="scss">
.flexbox {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.controls-container {
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 20px 0;
}

.fallback-msg-ct {
  margin-top: 30%;
  margin-right: 50%;
  margin-left: 50%;
  width: 300px;
  font-size: 16px;
}

.preview-body {
  display: flex;
  align-items: flex-start;
  gap: 24px;
}

.inapp-preview-container {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 20px 0;
  min-height: 400px;
  width: 100%;
}

.inapp-preview-container--split {
  width: 50%;
}

.preview-details {
  width: 50%;
  padding-top: 20px;
  font-size: 12px;
  color: #606266;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.native-preview-container {
  padding: 20px 0;
}

.native-payload {
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  overflow: hidden;
}

.native-payload-header {
  background-color: #f5f7fa;
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 600;
  color: #606266;
  border-bottom: 1px solid #e4e7ed;
}

.native-payload-content {
  padding: 16px;
  margin: 0;
  font-size: 12px;
  line-height: 1.6;
  color: #303133;
  background-color: #fafafa;
  overflow-x: auto;
  white-space: pre-wrap;
  word-break: break-word;
}

.html-content {
  width: 100%;
  height: 100%;
  overflow: hidden;
  background: url('../../../../img/checker-transparency.jpg') repeat;
}

.inapp-html-preview {
  border: 0;
  box-sizing: border-box;
}
</style>



// WEBPACK FOOTER //
// src/modules/campaign-manage/components/preview/PreviewContentInApp.vue